import { useState } from "react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { MenuItemImage } from "@/components/menu/MenuItemImage";
import { PriceDisplay } from "@/components/menu/PriceDisplay";
import { useI18n } from "@/lib/i18n";
import { formatPrice, loc } from "@/lib/format";
import type { MenuItem } from "@/lib/types";
import { cn } from "@/lib/utils";

export function MenuItemPreviewDialog({
  item,
  open,
  onOpenChange,
  priceMode,
}: {
  item: MenuItem;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  priceMode: "indoor" | "online";
}) {
  const { lang, t } = useI18n();
  const [variationId, setVariationId] = useState(item.variations[0]?.id ?? "");
  const variation = item.variations.find((option) => option.id === variationId) ?? item.variations[0];
  const description = loc(variation?.description, lang) || loc(item.description, lang);
  const price = priceMode === "indoor"
    ? (variation?.branchPrice ?? item.branchPrice)
    : (variation?.price ?? item.price);
  const originalPrice = priceMode === "online"
    ? (variation?.originalPrice ?? item.originalPrice)
    : null;
  const groups = variation?.additionGroups ?? item.additionGroups;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-h-[min(90dvh,760px)] max-w-xl gap-0 overflow-y-auto rounded-3xl p-0 [&>button]:bg-background/95 [&>button]:opacity-100">
        <MenuItemImage
          source={variation?.responsiveImage ?? variation?.image ?? item.responsiveImage ?? item.image}
          name={loc(item.name, lang)}
          sizes="(max-width: 640px) calc(100vw - 2rem), 576px"
          className="h-auto w-full"
          fallbackClassName="aspect-[4/3] sm:aspect-[16/10]"
        />
        <div className="grid gap-5 p-5 sm:p-6">
          <DialogHeader className="gap-2">
            <DialogTitle className="text-2xl font-black leading-tight">
              {loc(item.name, lang)}
            </DialogTitle>
            <DialogDescription className="text-sm leading-relaxed">
              {description || t("branchMenu.readonly")}
            </DialogDescription>
          </DialogHeader>

          {item.type === "variable" && item.variations.length > 0 ? (
            <div className="grid gap-2">
              <h3 className="text-sm font-bold">{t("menu.chooseVariation")}</h3>
              <div className="flex flex-wrap gap-2">
                {item.variations.map((option) => (
                  <button
                    key={option.id}
                    type="button"
                    onClick={() => setVariationId(option.id)}
                    aria-pressed={option.id === variation?.id}
                    className={cn(
                      "min-h-11 rounded-full border px-4 py-2 text-sm font-semibold transition-colors focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-primary",
                      option.id === variation?.id
                        ? "border-primary bg-primary text-primary-foreground"
                        : "border-border bg-card hover:border-primary/50",
                    )}
                  >
                    {loc(option.name, lang)}
                  </button>
                ))}
              </div>
            </div>
          ) : null}

          {groups.length > 0 ? (
            <div className="grid gap-4 border-t border-border pt-4">
              {groups.map((group) => (
                <div key={group.id} className="grid gap-2">
                  <h3 className="text-sm font-bold">{loc(group.name, lang)}</h3>
                  <ul className="grid gap-1.5 text-sm text-muted-foreground">
                    {group.additions.map((addition) => (
                      <li key={addition.id} className="flex justify-between gap-3">
                        <span>{loc(addition.name, lang)}</span>
                        <span className="shrink-0 tabular-nums">
                          {addition.price ? `+${formatPrice(addition.price, lang)}` : t("menu.included")}
                        </span>
                      </li>
                    ))}
                  </ul>
                </div>
              ))}
            </div>
          ) : null}

          <div className="border-t border-border pt-4 text-xl font-extrabold">
            <PriceDisplay price={price} originalPrice={originalPrice} />
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
